const destinations = [
  {
    name: "Mexico (Sonora, Rocky Point, CDMX)",
    context: "Tucson residents travel frequently to Nogales, Puerto Peñasco, Hermosillo, and Mexico City — via land border (60 mi to Nogales) or TUS flights",
    urgency: "Weekend and holiday border crossing plans often arise 1–2 weeks in advance",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Spain, Germany & France",
    context: "UA study-abroad semesters in Salamanca, Hamburg, and Paris; also UA faculty and Raytheon international partner meetings",
    urgency: "Study-abroad program departures have fixed orientation dates that standard processing cannot meet",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Japan & South Korea",
    context: "Davis-Monthan PCS assignments to Misawa, Kadena, and Osan; also Raytheon defense partner meetings in Japan and South Korea",
    urgency: "PCS orders arrive with 30 days or less to report — dependent passports must be current",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "United Kingdom & Australia",
    context: "Raytheon, L3Harris, and defense contractor travel to UK and Australian government partners; also UA research collaborations",
    urgency: "Government contract meetings have fixed schedules with no flexibility",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Costa Rica & Latin America",
    context: "UA ecology, biology, and archaeology field research programs; also Tucson retiree ecotourism and medical tourism travel",
    urgency: "Field research programs depart on fixed academic calendar dates",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Chile & International Observatory Sites",
    context: "UA Steward Observatory astronomers and researchers travel to Las Campanas Observatory, ALMA, and other international telescope sites",
    urgency: "Observing runs are scheduled months in advance but passport expiration is often discovered late",
    icon: "ri-map-pin-2-line",
  },
];

const stats = [
  { stat: "120 mi", label: "to the Phoenix Passport Agency from Tucson — we eliminate that trip" },
  { stat: "35,000+", label: "University of Arizona students — many needing first-time passports for study abroad" },
  { stat: "14–30 Days", label: "typical notice Davis-Monthan families receive before PCS orders" },
  { stat: "24 hrs", label: "fastest turnaround for genuine Tucson emergency passport cases" },
];

export default function TucsonDestinationsSection() {
  return (
    <section className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Where Tucson Travelers Go — And Why Passports Get Urgent
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            These are the destinations and travel patterns we see most from Southern Arizona clients. Understanding local travel behavior is part of why our document review catches issues before they derail your PCS move, study-abroad semester, or Mexico weekend.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {stats.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {destinations.map((d, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{d.name}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">{d.context}</p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">{d.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Explore Other Arizona Service Areas</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                We serve travelers across Arizona with the same expedited passport processing and document review expertise. Looking for service in another community?
              </p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <a href="/phoenix" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Phoenix <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/scottsdale" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Scottsdale <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/marana" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Marana <i className="ri-arrow-right-line text-xs"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}